import { Link } from 'react-router-dom';
import { usePages, useSettings } from '../../hooks/useCMS';
import { GraduationCap, Globe, Video, MessageCircle, Mail } from 'lucide-react';

export function Footer() {
  const { data: pages } = usePages();
  const { data: settings } = useSettings();

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-slate-300 mt-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">{settings?.site_name || 'Study Bangladesh'}</h3>
                <p className="text-xs text-slate-400">{settings?.site_tagline || 'শিক্ষা সবার জন্য'}</p>
              </div>
            </Link>
            <p className="text-sm text-slate-400">
              {settings?.site_description || 'The most student-friendly MCQ exam platform in Bangladesh'}
            </p>
            {/* Social Links */}
            <div className="flex gap-3">
              {settings?.facebook_url && (
                <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors">
                  <Globe className="w-4 h-4" />
                </a>
              )}
              {settings?.youtube_url && (
                <a href={settings.youtube_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors">
                  <Video className="w-4 h-4" />
                </a>
              )}
              {settings?.telegram_url && (
                <a href={settings.telegram_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-sky-500 transition-colors">
                  <MessageCircle className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/exams" className="text-sm hover:text-white transition-colors">Exams</Link>
              </li>
              <li>
                <Link to="/videos" className="text-sm hover:text-white transition-colors">Video Lessons</Link>
              </li>
              <li>
                <Link to="/leaderboard" className="text-sm hover:text-white transition-colors">Leaderboard</Link>
              </li>
              <li>
                <Link to="/groups" className="text-sm hover:text-white transition-colors">Study Groups</Link>
              </li>
            </ul>
          </div>

          {/* CMS Pages */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-2">
              {pages?.map((page: any) => (
                <li key={page.id}>
                  <Link 
                    to={`/page/${page.slug}`} 
                    className="text-sm hover:text-white transition-colors"
                  >
                    {page.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/contact" className="text-sm hover:text-white transition-colors">Contact Us</Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-2">
              {settings?.contact_email && (
                <li className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-primary-400" />
                  <a href={`mailto:${settings.contact_email}`} className="hover:text-white transition-colors">
                    {settings.contact_email}
                  </a>
                </li>
              )}
              {settings?.contact_phone && (
                <li className="flex items-center gap-2 text-sm">
                  <span className="text-primary-400">📞</span>
                  <a href={`tel:${settings.contact_phone}`} className="hover:text-white transition-colors">
                    {settings.contact_phone}
                  </a>
                </li>
              )}
              {settings?.contact_address && (
                <li className="flex items-start gap-2 text-sm">
                  <span className="text-primary-400 mt-0.5">📍</span>
                  <span>{settings.contact_address}</span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            {settings?.footer_text || `© ${currentYear} Study Bangladesh. All rights reserved.`}
          </p>
          <div className="flex gap-6">
            <Link to="/page/privacy-policy" className="text-sm text-slate-500 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/page/terms-of-service" className="text-sm text-slate-500 hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
